/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { platformMcpDismissOnboarding } from "../funcs/platformMcpDismissOnboarding.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DismissPlatformMCPOnboardingRequest,
  DismissPlatformMCPOnboardingSecurity,
} from "../models/operations/dismissplatformmcponboarding.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DismissPlatformMCPOnboardingMutationVariables = {
  request?: DismissPlatformMCPOnboardingRequest | undefined;
  security?: DismissPlatformMCPOnboardingSecurity | undefined;
  options?: RequestOptions;
};

export type DismissPlatformMCPOnboardingMutationData = void;

export type DismissPlatformMCPOnboardingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * dismissOnboarding platformMcp
 *
 * @remarks
 * Dismiss the optional current user's Platform MCP onboarding workflow without changing organization setup or project resources.
 */
export function useDismissPlatformMCPOnboardingMutation(
  options?: MutationHookOptions<
    DismissPlatformMCPOnboardingMutationData,
    DismissPlatformMCPOnboardingMutationError,
    DismissPlatformMCPOnboardingMutationVariables
  >,
): UseMutationResult<
  DismissPlatformMCPOnboardingMutationData,
  DismissPlatformMCPOnboardingMutationError,
  DismissPlatformMCPOnboardingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDismissPlatformMCPOnboardingMutation(client, options),
    ...options,
  });
}

export function mutationKeyDismissPlatformMCPOnboarding(): MutationKey {
  return ["@gram/client", "platformMcp", "dismissOnboarding"];
}

export function buildDismissPlatformMCPOnboardingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DismissPlatformMCPOnboardingMutationVariables,
  ) => Promise<DismissPlatformMCPOnboardingMutationData>;
} {
  return {
    mutationKey: mutationKeyDismissPlatformMCPOnboarding(),
    mutationFn: function dismissPlatformMCPOnboardingMutationFn({
      request,
      security,
      options,
    }): Promise<DismissPlatformMCPOnboardingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(platformMcpDismissOnboarding(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
